/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import {
  authCheckCallApi,
  DateFormatLong,
  refreshToken,
} from "../../Authenticator/utils";
import {
  Button,
  Icon,
  Dimmer,
  Loader,
  Modal,
  Transition,
  Grid,
  Checkbox,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "../../Components.module.css";
import { useMutation } from "@apollo/react-hooks";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import {
  SuspendPlanMutation,
  ReactivatePlanMutation,
} from "../../../graphql/organization";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";

interface ChangeSubscriptionDialogProps {
  orgId: string;
  isCancel: boolean;
  onCloseDialog: any;
  manage?: boolean;
}

const ChangeSubscriptionDialog: React.FC<ChangeSubscriptionDialogProps> = ({
  orgId,
  isCancel,
  onCloseDialog,
  manage
}) => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [loading, setLoading] = useState(false);
  const [expirationDate, setExpirationDate] = useState<number | null>(null);
  const [expireImmediately, setExpireImmediately] = useState<boolean>(false);
  const cancelTitle = "Cancel Subscription";
  const resumeTitle = "Resume Subscription";
  const cancelFailedMessage =
    "Unable to cancel subscription, please try again later.";

  const [previewSuspendPlan, { loading: previewLoading }] = useMutation(
    SuspendPlanMutation,
    {
      variables: {
        options: {
          orgId: orgId,
          preview: true,
        },
      },
      onError: () => {
        setMessage({
          title: cancelTitle,
          message: cancelFailedMessage,
        });
      },
      onCompleted: (data) => {
        if (data && data.suspendPlan && data.suspendPlan.expires) {
          setExpirationDate(data.suspendPlan.expires);
        }
      },
    }
  );

  useEffect(() => {
    if (isCancel) {
      authCheckCallApi(previewSuspendPlan);
    }
  }, [previewSuspendPlan, isCancel]);

  const onRefreshTokenComplete = () => {
    window.location.reload();
  };

  const onChangeSubscriptionDone = () => {
    if (!manage) {
      refreshToken(onRefreshTokenComplete);
    } else {
      onCloseDialog();
    }
  };

  const [suspendPlan] = useMutation(SuspendPlanMutation, {
    variables: {
      options: {
        orgId: orgId,
        preview: false,
        ...(manage && {
          expires: expirationDate,
          immediateExpiry: expireImmediately,
        }),
      },
    },
    onError: () => {
      setLoading(false);
      setMessage({
        title: cancelTitle,
        message: cancelFailedMessage,
      });
    },
    onCompleted: () => onChangeSubscriptionDone(),
  });

  const [reactivatePlan] = useMutation(ReactivatePlanMutation, {
    variables: {
      orgId: orgId,
    },
    onError: () => {
      setLoading(false);
      setMessage({
        title: resumeTitle,
        message:
          "Unable to resume subscription at this time, please try again later.",
      });
    },
    onCompleted: () => onChangeSubscriptionDone(),
  });

  const onChangeSubscriptionConfirmed = () => {
    setLoading(true);
    if (isCancel) {
      // Make sure that expiration date is at least 1 min. in the future.
      if (manage && expirationDate) {
        const minExpirationDate = new Date();
        minExpirationDate.setMinutes(minExpirationDate.getMinutes() + 1);
        if (expirationDate < minExpirationDate.getTime()) {
          setExpirationDate(minExpirationDate.getTime());
        }
      }

      authCheckCallApi(suspendPlan);
    } else {
      authCheckCallApi(reactivatePlan);
    }
  };

  const getExpirationDate = () => {
    if (expirationDate !== null) {
      if (manage) {
        return (
          <div className={styles.subscriptionMessage}>
            <Checkbox
              style={{ marginTop: "1rem" }}
              label="Expire this subscription immediately"
              onChange={() => setExpireImmediately(!expireImmediately)}
              checked={expireImmediately}
            />

            {
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "baseline",
                }}
              >
                <div style={{ marginTop: "1rem", marginRight: "2rem" }}>
                  Set the expiration date for this subscriptions
                </div>
                <DatePicker
                  showTimeInput
                  disabled={expireImmediately}
                  minDate={new Date()}
                  selected={new Date(expirationDate)}
                  onChange={(date: Date) => {
                    setExpirationDate(Number(date));
                  }}
                  customInput={
                    <Button
                      primary
                      className={cStyles.buttonPrime}
                      icon
                      labelPosition="left"
                    >
                      <Icon name="calendar alternate outline" />
                      {new Date(expirationDate).toLocaleDateString(
                        navigator.language,
                        DateFormatLong
                      )}
                    </Button>
                  }
                />
              </div>
            }
          </div>
        );
      }

      return (
        <div className={styles.subscriptionMessage}>
          The current subscription will remain active until{" "}
          {new Date(expirationDate).toLocaleDateString(navigator.language)}
        </div>
      );
    }

    return null;
  };

  return (
    <Transition animation="fade in" duration="250">
      <Modal size="small" open>
        {(loading || previewLoading) && (
          <Dimmer active={true}>
            <Loader active={true} size="large" />
          </Dimmer>
        )}

        <Grid stackable padded style={{ padding: "15px" }}>
          <Grid.Row>
            <Grid.Column width={14}>
              <span className={cStyles.title}>
                {(isCancel && cancelTitle) || resumeTitle}
              </span>
            </Grid.Column>

            <Grid.Column width={2} floated="right">
              <div
                className={cStyles.closeButton}
                onClick={() => onCloseDialog()}
              >
                <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
              </div>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              {(isCancel && (
                <div>
                  <div className={styles.subscriptionMessage}>
                    Are you sure you want to cancel the subscription?
                  </div>
                  {getExpirationDate()}
                </div>
              )) || (
                <p className={styles.subscriptionMessage}>
                  Resume the current subscription?
                </p>
              )}
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={{ marginTop: "2rem" }}>
            <Grid.Column width={8}>
              <Button
                secondary
                className={cStyles.buttonSecond}
                onClick={() => onCloseDialog()}
              >
                <Ico name="cancel" style={{ marginRight: "10px" }} />
                Cancel
              </Button>
            </Grid.Column>

            <Grid.Column width={8} floated="right" textAlign="right">
              <Button
                primary
                className={cStyles.buttonPrime}
                onClick={() => {
                  onChangeSubscriptionConfirmed();
                }}
              >
                {(isCancel && cancelTitle) || resumeTitle}
                <Ico
                  name={isCancel ? "cancel" : "check"}
                  style={{ marginLeft: "10px" }}
                />
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => {
              setMessage(null);
              onCloseDialog();
            }}
          />
        )}
      </Modal>
    </Transition>
  );
};

export default ChangeSubscriptionDialog;
